import type { BoardObjectV2 } from "@bezpart-board/shared";

export function zIndexKey(obj: { zIndex?: string }): string {
  return typeof obj.zIndex === "string" ? obj.zIndex : "";
}

/** Числовое сравнение, чтобы "10" > "6" (не lexicographic). */
export function compareZIndex(a: string, b: string): number {
  if (a === b) return 0;
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return a < b ? -1 : 1;
}

/** Сортировка по zIndex, затем порядок в массиве. */
export function sortedByZIndex(objects: BoardObjectV2[]): BoardObjectV2[] {
  return objects
    .map((obj, index) => ({ obj, index }))
    .sort((a, b) => {
      const c = compareZIndex(zIndexKey(a.obj), zIndexKey(b.obj));
      if (c !== 0) return c;
      return a.index - b.index;
    })
    .map(({ obj }) => obj);
}

/**
 * Слой ниже всех: сюда ложится картинка при вставке.
 *
 * Картинку на урок приносят как материал — снимок задачи, карту, чертёж, —
 * и поверх неё пишут. Если она ложится сверху, как любой новый объект, то
 * накрывает собой всё, что уже было, а каждый новый штрих приходится
 * вручную поднимать. Поэтому у неё единственной порядок обратный.
 */
export function bottomZIndex(objects: { zIndex?: string }[]): string {
  let minN = 1;
  for (const o of objects) {
    const n = Number(zIndexKey(o));
    if (Number.isFinite(n) && n < minN) minN = n;
  }
  return String(minN - 1);
}

export function nextZIndex(objects: { zIndex?: string }[]): string {
  let maxN = 0;
  for (const o of objects) {
    const n = Number(zIndexKey(o));
    if (Number.isFinite(n) && n > maxN) maxN = n;
  }
  return String(maxN + 1);
}

function numericZ(obj: { zIndex?: string }, fallback: number): number {
  const n = Number(zIndexKey(obj));
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Меняем zIndex только у затронутых объектов (обычно выделение ± сосед).
 * Нельзя перенумеровывать всю сцену — на доске 200+ объектов API режет batch (>100 ops).
 */
export function computeZOrderPatches(
  objects: BoardObjectV2[],
  selectedIds: string[],
  direction: "forward" | "backward" | "front" | "back"
): { id: string; patch: { zIndex: string } }[] {
  const selected = new Set(selectedIds.filter(Boolean));
  if (!selected.size) return [];

  const ordered = sortedByZIndex(objects);
  const selectedOrdered = ordered.filter((o) => selected.has(o.id));
  if (!selectedOrdered.length) return [];

  const patches: { id: string; patch: { zIndex: string } }[] = [];
  const push = (id: string, next: string) => {
    const prev = objects.find((o) => o.id === id);
    if (!prev) return;
    if (zIndexKey(prev) === next) return;
    patches.push({ id, patch: { zIndex: next } });
  };

  if (direction === "front") {
    // Выделение поверх всех, относительный порядок внутри выделения сохраняем
    let z = Number(nextZIndex(objects));
    for (const obj of selectedOrdered) {
      push(obj.id, String(z++));
    }
    return patches;
  }

  if (direction === "back") {
    const others = ordered.filter((o) => !selected.has(o.id));
    if (!others.length) return [];
    let minOther = Infinity;
    others.forEach((o, i) => {
      const n = numericZ(o, i + 1);
      if (n < minOther) minOther = n;
    });
    if (!Number.isFinite(minOther)) minOther = 1;
    // Ниже остальных: …, minOther-2, minOther-1
    let z = minOther - selectedOrdered.length;
    for (const obj of selectedOrdered) {
      push(obj.id, String(z++));
    }
    return patches;
  }

  // Один шаг: меняем местами z только с соседом вне выделения
  if (direction === "forward") {
    for (let i = ordered.length - 2; i >= 0; i--) {
      const cur = ordered[i]!;
      const above = ordered[i + 1]!;
      if (!selected.has(cur.id) || selected.has(above.id)) continue;
      const zCur = numericZ(cur, i + 1);
      let zAbove = numericZ(above, i + 2);
      if (zAbove <= zCur) zAbove = zCur + 1;
      push(cur.id, String(zAbove));
      push(above.id, String(zCur));
    }
    return patches;
  }

  // backward
  for (let i = 1; i < ordered.length; i++) {
    const cur = ordered[i]!;
    const below = ordered[i - 1]!;
    if (!selected.has(cur.id) || selected.has(below.id)) continue;
    const zCur = numericZ(cur, i + 1);
    let zBelow = numericZ(below, i);
    if (zBelow >= zCur) zBelow = zCur - 1;
    push(cur.id, String(zBelow));
    push(below.id, String(zCur));
  }
  return patches;
}
